// Links in text that comes from customers, carriers or the model.
//
// Security (ISO 27001 A.8.26): nothing here builds HTML. `splitLinks` returns
// plain segments that the caller renders as React text nodes and <a> elements,
// and every href passes `webHref` (http/https only — never javascript:, data:,
// vbscript:, file:…). mailto:/tel: are deliberately not linked (product
// decision 2026-10-14).

// Links that come from the web through the model or from a message: http(s) only.
export const webHref = (url) => (typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : undefined);

// A candidate starts at http(s):// or www. and runs until whitespace or a
// character that cannot end a URL in prose: < > " (so `<https://…>` and a raw
// `<a href="…">` keep their markup as text). Linear: no nested quantifier.
const CANDIDATE = /(?:https?:\/\/|www\.)[^\s<>"]+/gi;

// Prose punctuation that ends a sentence, not the URL.
const TRAILING_PUNCTUATION = '.,;:!?»"\'';
const CLOSING_TO_OPENING = { ')': '(', ']': '[', '}': '{' };

// A `www.` inside a word, an e-mail address or a path is not a link of its own.
const INSIDE_TOKEN = /[\w.@/\\-]/;

const count = (text, char) => {
    let n = 0;
    for (let i = 0; i < text.length; i += 1) if (text[i] === char) n += 1;
    return n;
};

// Strip the trailing punctuation and the closing brackets the URL did not open
// (`(voir https://x.fr/a).` → `https://x.fr/a`), keep balanced ones
// (`https://fr.wikipedia.org/wiki/Colis_(poste)`).
const trimTrailing = (value) => {
    let end = value.length;
    const balance = {};
    Object.entries(CLOSING_TO_OPENING).forEach(([close, open]) => {
        balance[close] = count(value, open) - count(value, close);
    });
    while (end > 0) {
        const last = value[end - 1];
        if (TRAILING_PUNCTUATION.includes(last)) {
            end -= 1;
        } else if (last in CLOSING_TO_OPENING && balance[last] < 0) {
            balance[last] += 1;
            end -= 1;
        } else {
            break;
        }
    }
    return value.slice(0, end);
};

// A link needs a host: `https://` alone or `www.` alone stays text.
const HAS_HOST = /^(?:https?:\/\/[^/?#\s]+|www\.[^./?#\s]+\.[^./?#\s]+)/i;

// Split `text` into [{ type: 'text', value }, { type: 'link', value, href }…].
// `value` is what the reader sees (the original spelling), `href` the safe target
// (`www.…` → `https://www.…`). Consecutive text is merged into one segment.
export const splitLinks = (text) => {
    const source = typeof text === 'string' ? text : '';
    const segments = [];
    const pushText = (value) => {
        if (!value) return;
        const previous = segments[segments.length - 1];
        if (previous && previous.type === 'text') previous.value += value;
        else segments.push({ type: 'text', value });
    };

    let cursor = 0;
    CANDIDATE.lastIndex = 0;
    let match = CANDIDATE.exec(source);
    while (match) {
        const start = match.index;
        const isWww = match[0][0] === 'w' || match[0][0] === 'W';
        const value = trimTrailing(match[0]);
        const href = HAS_HOST.test(value)
            && !(isWww && start > 0 && INSIDE_TOKEN.test(source[start - 1]))
            ? webHref(isWww ? `https://${value}` : value)
            : undefined;
        if (href) {
            pushText(source.slice(cursor, start));
            segments.push({ type: 'link', value, href });
            cursor = start + value.length;
        }
        match = CANDIDATE.exec(source);
    }
    pushText(source.slice(cursor));
    return segments;
};
